
import { format, addHours } from 'date-fns';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { deleteBookingById } from '@/store/schedule/useScheduledClasses';
import i18next from 'i18next';
import { getUserEmail, getBookingsForClassAndUser } from '@/services/userProfileService';
import { validateBookingInterval } from '@/services/bookingValidationService';
import { logUserActivity } from '@/utils/logUserActivity';
import { useUserStore } from '@/store/userStore';
import { bookClassWithRetry } from '@/utils/bookingUtils';
import { getUserGymId } from '@/utils/gymAuth';
import { getCurrentUserId } from '@/utils/authHelpers';
import { bookingSuccessToast, bookingErrorToast } from '@/utils/bookingToast';

const NO_SESSIONS_AVAILABLE_MESSAGE = i18next.t('booking.noSessionsAvailable', { ns: 'bookingui' });
const CALENDAR_NOT_ALLOWED_MESSAGE = i18next.t('booking.calendarNotAllowedMessage', { ns: 'bookingui' });

const getBookingBusinessErrorCode = (error: unknown): string | undefined => {
  const code = (error as { code?: string })?.code;
  // Les réponses HTTP 200 des edge functions portent le libellé dans un champ
  // `error` de type chaîne, pas dans `message` : sans ça on retombait sur
  // "[object Object]" et le message métier était perdu.
  const bodyError = (error as { error?: unknown })?.error;
  const message =
    (error as { message?: string })?.message ||
    (typeof bodyError === 'string' ? bodyError : '') ||
    String(error);
  if (code === 'NO_SESSIONS_AVAILABLE' || message.includes('NO_SESSIONS_AVAILABLE')) return 'NO_SESSIONS_AVAILABLE';
  if (code === 'NO_VALID_SUBSCRIPTION_FOR_DATE' || message.includes('NO_VALID_SUBSCRIPTION_FOR_DATE')) return 'NO_VALID_SUBSCRIPTION_FOR_DATE';
  if (code === 'CALENDAR_NOT_ALLOWED' || message.includes('CALENDAR_NOT_ALLOWED')) return 'CALENDAR_NOT_ALLOWED';
  return undefined;
};

// Simplified translation function to replace useTranslation hook
const translate = (key: string, ns?: string): string => {
  return i18next.t(key, { ns });
};

/**
 * Résultat d'une confirmation de réservation.
 * `confirmed` = nombre de réservations réellement écrites en base ; il pilote
 * les effets « de succès » côté appelant (son, slot récemment réservé,
 * fermeture du dialogue), qui s'exécutaient jusqu'ici même après un échec.
 */
export interface BookingConfirmationOutcome {
  confirmed: number;
  failed: number;
  removed: number;
}

const EMPTY_OUTCOME: BookingConfirmationOutcome = { confirmed: 0, failed: 0, removed: 0 };

/**
 * Hook to handle booking confirmations, both for admin and regular users
 */
export const useBookingConfirmation = (
  classId: string,
  selectedClass: any | null,
  selectedDate: Date,
  selectedTime: string,
  onOpenChange: (open: boolean) => void,
  onConfirm?: () => void
) => {
  // Confirm booking for admin (multiple attendees) or regular user
  const confirmBooking = async (
    isAdmin: boolean,
    filteredAttendees: { id: string; name: string; selected: boolean }[] = [],
    initialSelectedAttendees: Set<string> = new Set(),
    existingBookings: { userId: string; bookingId: string; status: string }[] = []
  ): Promise<BookingConfirmationOutcome> => {
    if (!selectedClass) {
      bookingErrorToast(translate('booking.classNotFound', 'athlete'));
      return { ...EMPTY_OUTCOME, failed: 1 };
    }

    // ✅ Le coach peut désormais réserver pour les adhérents de sa salle
    // (même flux que l'admin) — les gardes serveur de bookings-management
    // restent la référence (staff jamais pour lui-même, client jamais tiers).
    
    try {
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      
      // Check if the class starts within the next hour (only for athletes, not for admins)
      if (!isAdmin) {
        const [hours, minutes] = selectedTime.split(':').map(Number);
        const classStartTime = new Date(selectedDate);
        classStartTime.setHours(hours, minutes, 0, 0);
        
        const currentTimePlusOneHour = addHours(new Date(), 1);
        
        if (classStartTime <= currentTimePlusOneHour) {
          bookingErrorToast(translate('booking.tooLate', 'athlete'));
          return { ...EMPTY_OUTCOME, failed: 1 };
        }
      }

      let outcome: BookingConfirmationOutcome = EMPTY_OUTCOME;

      // For admin booking multiple attendees
      if (isAdmin && filteredAttendees.length > 0) {
        outcome = await handleAdminBookingConfirmation(
          formattedDate, 
          filteredAttendees, 
          initialSelectedAttendees, 
          existingBookings
        );
      } 
      // For regular user booking
      else if(!isAdmin) {
        const booked = await handleUserBookingConfirmation(formattedDate);
        outcome = booked
          ? { confirmed: 1, failed: 0, removed: 0 }
          : { confirmed: 0, failed: 1, removed: 0 };
      }
      
      if (onConfirm) {
        onConfirm();
      }

      return outcome;
    } catch (error) {
      console.error('Error in confirmBooking:', error);
      bookingErrorToast(translate('booking.error', 'athlete'));
      return { ...EMPTY_OUTCOME, failed: 1 };
    }
  };

  // Handle admin booking confirmations (multiple attendees)
  const handleAdminBookingConfirmation = async (
    formattedDate: string,
    filteredAttendees: { id: string; name: string; selected: boolean }[],
    initialSelectedAttendees: Set<string>,
    existingBookings: { userId: string; bookingId: string; status: string }[]
  ): Promise<BookingConfirmationOutcome> => {
    const selectedAttendees = filteredAttendees.filter(a => a.selected);
    const selectedAttendeeIds = new Set(selectedAttendees.map(a => a.id));

    // Compteurs de succès RÉELS. Le toast final annonçait
    // `selectedAttendees.length` réservations, y compris pour les participants
    // dont l'écriture a échoué ou qui étaient déjà inscrits (audit lot 7).
    let confirmedCount = 0;
    let failedCount = 0;
    let removedCount = 0;
    let alreadyBookedCount = 0;

    // Supprimer les bookings des désélectionnés
    const attendeesToRemove = Array.from(initialSelectedAttendees).filter(id => !selectedAttendeeIds.has(id));
    const newAttendees = selectedAttendees.filter(a => !initialSelectedAttendees.has(a.id));

    // 🛡️ Garde-fou : si on s'apprête à la fois à supprimer des bookings ET à en
    // créer de nouveaux, c'est suspect (souvent un bug de filtrage côté caller).
    // On loggue pour diagnostic mais on n'altère pas la logique : la
    // désélection volontaire reste un usage légitime.
    if (attendeesToRemove.length > 0 && newAttendees.length > 0) {
      console.warn('[handleAdminBookingConfirmation] ⚠️ Suppression + création simultanées détectées', {
        toRemove: attendeesToRemove.length,
        toCreate: newAttendees.length,
        initialCount: initialSelectedAttendees.size,
        selectedCount: selectedAttendees.length,
        passedListSize: filteredAttendees.length,
      });
    }

    for (const attendeeId of attendeesToRemove) {
      const bookingToDelete = existingBookings.find(b => b.userId === attendeeId);
      if (bookingToDelete) {
        const success = await deleteBookingById(bookingToDelete.bookingId);
        if (!success) {
          bookingErrorToast(translate('booking.deleteError', 'athlete'));
          failedCount++;
        }
        else{
          removedCount++;
          logUserActivity('BOOKING_CANCELLED', `classId:${classId},admin`, attendeeId);
        }
      }
    }
  
    // Pour chaque participant sélectionné
    for (const attendee of selectedAttendees) {
      // Cherche s'il a déjà un booking (quel que soit le statut)
      const existingBooking = existingBookings.find(
        b => b.userId === attendee.id
      );
  
      // Fetch user profile via edge function
      const gymId = getUserGymId();
      if (!gymId) {
        console.error('No gym_id available');
        failedCount++;
        continue;
      }

      const { data: profileData } = await supabase.functions.invoke('user-management', {
        body: {
          action: 'get_user_profile',
          gym_id: gymId,
          userId: attendee.id
        }
      });

      const userProfile = profileData?.profile;

      // `getUserEmail` renvoie `null` en cas d'échec (et son edge function peut
      // tomber) : le `.email` direct levait un TypeError qui remontait au catch
      // de `confirmBooking` et avortait TOUTE la boucle — aucune réservation
      // créée, ni pour ce participant ni pour les suivants (audit lot 7).
      // L'e-mail est un accessoire : son absence ne doit jamais bloquer.
      const userEmail = (await getUserEmail(attendee.id).catch((err) => {
        console.error('[handleAdminBookingConfirmation] getUserEmail failed', err);
        return null;
      }))?.email ?? null;

      // 1. Cas où il n'y a pas de booking → INSERT
      if (!existingBooking) {
        const durationInt = typeof selectedClass.duration === 'string' 
          ? parseInt(selectedClass.duration, 10) 
          : (selectedClass.duration ?? 45);
        
        const gymId = selectedClass.gym_id || useUserStore.getState().gymId;
        
        if (!gymId) {
          console.error('[book_class_slot] Missing gym_id', {
            classId,
            selectedClass_gym_id: selectedClass.gym_id,
          userStore_gymId: useUserStore.getState().gymId
          });
          bookingErrorToast(translate('booking.gymError', 'athlete'));
          failedCount++;
          continue;
        }


        const toastId = `booking-${attendee.id}`;
        toast.loading(translate('booking.inProgress', 'athlete'), { id: toastId });

        let booked = false;
        let error;

        try {
          // `bookClassWithRetry` renvoie un booléen : `false` = rien n'a été
          // écrit. Ne tester que `error` laissait passer ce cas et enchaînait
          // log BOOKING_CREATED + e-mail + toast (audit 3.1).
          booked = (await bookClassWithRetry({
            classId,
            userId: attendee.id,
            date: formattedDate,
            time: selectedTime,
            duration: durationInt,
            gymId
          })) === true;
          toast.dismiss(toastId);
        } catch (err: any) {
          error = err;
          toast.dismiss(toastId);
        }

        if (!error && !booked) {
          // Cas « échec silencieux » : pas d'exception mais rien en base.
          console.error('[book_class_slot] returned falsy without error', {
            classId,
            attendeeId: attendee.id,
          });
          logUserActivity('BOOKING_FAILED', `classId:${classId},admin,error:no_row_created`, attendee.id);
          bookingErrorToast(`${translate('booking.error', 'athlete')}: ${attendee.name}`);
          failedCount++;
          continue;
        }

        if (error) {
          console.error('[book_class_slot] RPC error:', error, {
            code: (error as any).code,
            message: error.message,
            details: (error as any).details
          });
          
          logUserActivity('BOOKING_FAILED', `classId:${classId},admin,error:${error.message?.slice(0, 150)}`, attendee.id);
          
          const businessCode = getBookingBusinessErrorCode(error);
          if (businessCode === 'NO_SESSIONS_AVAILABLE') {
            bookingErrorToast(i18next.t('booking.noSessionsForAttendee', { ns: 'bookingui', name: attendee.name }));
          } else if (businessCode === 'NO_VALID_SUBSCRIPTION_FOR_DATE') {
            bookingErrorToast(i18next.t('booking.noValidSubscriptionForAttendee', { ns: 'bookingui', name: attendee.name }));
          } else if (businessCode === 'CALENDAR_NOT_ALLOWED') {
            bookingErrorToast(i18next.t('booking.calendarNotAllowedForAttendee', { ns: 'bookingui', name: attendee.name }));
          } else if (error.message?.includes('Class does not belong')) {
            bookingErrorToast(i18next.t('booking.classNotInGym', { ns: 'bookingui' }));
          } else {
            bookingErrorToast(`${translate('booking.error', 'athlete')}: ${attendee.name}`);
          }
          failedCount++;
        } else {
          confirmedCount++;
          logUserActivity("BOOKING_CREATED", `classId:${classId},admin`, attendee.id);
          
          // ✅ OPTIMISATION : Email NON-BLOQUANT (fire-and-forget)
          if (userProfile && userEmail) {
            supabase.functions.invoke('send-class-email', {
              body: {
                type: "booking",
                gym_id: gymId,
                user: {
                  email: userEmail,
                  firstName: userProfile.first_name || '',
                  lastName: userProfile.last_name || ''
                },
                classInfo: {
                  className: selectedClass.name,
                  date: format(selectedDate, 'dd/MM/yyyy'),
                  time: selectedTime,
                  coach: selectedClass.instructor,
                  duration: `${selectedClass.duration} min`
                }
              }
            }).catch(err => console.error('Error sending email confirmation:', err));
          }
        }
        continue;
      }
  
      // 2. Cas où il existe un booking annulé → UPDATE via edge function
      if (existingBooking.status === 'cancelled') {
        const { data: updateData, error } = await supabase.functions.invoke('bookings-management', {
          body: {
            action: 'update_booking',
            gym_id: gymId,
            bookingId: existingBooking.bookingId,
            data: {
              status: 'upcoming',
              class_date: formattedDate,
              time: selectedTime,
              duration: selectedClass.duration
            }
          }
        });
        // `update_booking` peut répondre avec un corps `{ success: false }`
        // (cours complet / doublon) : le champ `error` de supabase-js reste
        // alors nul et la réactivation était comptée comme un succès.
        const rejected = updateData?.success === false;
        if (error || rejected) {
          const businessCode = getBookingBusinessErrorCode(error ?? updateData);
          if (businessCode === 'NO_SESSIONS_AVAILABLE') {
            bookingErrorToast(i18next.t('booking.noSessionsForAttendee', { ns: 'bookingui', name: attendee.name }));
          } else if (businessCode === 'NO_VALID_SUBSCRIPTION_FOR_DATE') {
            bookingErrorToast(i18next.t('booking.noValidSubscriptionForAttendee', { ns: 'bookingui', name: attendee.name }));
          } else if (businessCode === 'CALENDAR_NOT_ALLOWED') {
            bookingErrorToast(i18next.t('booking.calendarNotAllowedForAttendee', { ns: 'bookingui', name: attendee.name }));
          } else {
            bookingErrorToast(`${translate('booking.updateError', 'athlete')}: ${attendee.name}`);
          }
          failedCount++;
        } else {
          confirmedCount++;
          logUserActivity("BOOKING_CREATED", `bookId:${existingBooking.bookingId},admin`, attendee.id);
          
          // ✅ OPTIMISATION : Email NON-BLOQUANT (fire-and-forget)
          if (userProfile && userEmail) {
            supabase.functions.invoke('send-class-email', {
              body: {
                type: "booking",
                gym_id: gymId,
                user: {
                  email: userEmail,
                  firstName: userProfile.first_name || '',
                  lastName: userProfile.last_name || ''
                },
                classInfo: {
                  className: selectedClass.name,
                  date: format(selectedDate, 'dd/MM/yyyy'),
                  time: selectedTime,
                  coach: selectedClass.instructor,
                  duration: `${selectedClass.duration} min`
                }
              }
            }).catch(err => console.error('Error sending email confirmation:', err));
          }
        }
        continue;
      }

      // 3. Booking déjà actif : rien à écrire, ce n'est pas une création.
      alreadyBookedCount++;
      continue;
    }

    // Message toast final — fondé sur les succès RÉELS, jamais sur la taille
    // de la sélection. Les échecs ont déjà toasté leur propre message.
    const outcome: BookingConfirmationOutcome = {
      confirmed: confirmedCount,
      failed: failedCount,
      removed: removedCount,
    };

    if (selectedAttendees.length === 0 && initialSelectedAttendees.size > 0) {
      bookingSuccessToast(translate('booking.allAttendeesRemoved', 'athlete'));
      return outcome;
    }

    if (confirmedCount > 0) {
      bookingSuccessToast(
        translate('booking.multipleBookingsSuccess', 'athlete')
          .replace('{{count}}', confirmedCount.toString())
      );
      return outcome;
    }

    if (failedCount > 0) {
      // Aucune réservation créée : ne rien annoncer de positif.
      return outcome;
    }

    if (removedCount > 0) {
      // Uniquement des désinscriptions : elles ont abouti.
      bookingSuccessToast(i18next.t('booking.participantsUpdated', { ns: 'bookingui' }));
      return outcome;
    }

    if (alreadyBookedCount > 0) {
      // Rien à créer : les participants sélectionnés étaient déjà inscrits.
      // On le dit plutôt que d'annoncer N réservations fantômes.
      bookingSuccessToast(i18next.t('booking.noChangeAlreadyBooked', { ns: 'bookingui' }));
    }

    return outcome;
  };
  

  // Handle regular user booking confirmations
  // Renvoie `true` uniquement si une réservation existe réellement en base
  // à la sortie. L'appelant s'en sert pour n'émettre son et fermeture de
  // dialogue qu'en cas de succès avéré.
  const handleUserBookingConfirmation = async (formattedDate: string): Promise<boolean> => {
    // Get current user ID
    const userId = await getCurrentUserId();
    
    if (!userId) {
      bookingErrorToast(translate('booking.authError', 'athlete'));
      return false;
    }
    
    // ✅ OPTIMISATION : Paralléliser validation + vérification bookings existants
    const [validationResult, bookingsForClassAndUser] = await Promise.all([
      validateBookingInterval(userId, formattedDate, selectedTime),
      getBookingsForClassAndUser(userId, classId, formattedDate)
    ]);
    
    if (!validationResult.isValid) {
      bookingErrorToast(validationResult.message || i18next.t('booking.mustWait48h', { ns: 'bookingui' }));
      return false;
    }
    
    const hasCancelledBooking = bookingsForClassAndUser.length > 0 && bookingsForClassAndUser[0].status === 'cancelled';

    if (bookingsForClassAndUser.length > 0 && bookingsForClassAndUser[0].status !== 'cancelled') {
      bookingErrorToast(translate('booking.alreadyBooked', 'athlete'));
      return false;
    }
    
    // Get user profile information via edge function
    const gymId = getUserGymId();
    if (!gymId) {
      console.error('No gym_id available');
      bookingErrorToast(translate('booking.gymError', 'athlete'));
      return false;
    }

    const { data: profileData } = await supabase.functions.invoke('user-management', {
      body: {
        action: 'get_user_profile',
        gym_id: gymId,
        userId: userId
      }
    });

    const userProfile = profileData?.profile;

    if(hasCancelledBooking){
      const { data: updateData, error: updateError } = await supabase.functions.invoke('bookings-management', {
        body: {
          action: 'update_booking',
          gym_id: gymId,
          bookingId: bookingsForClassAndUser[0].id,
          data: { status: 'upcoming' }
        }
      });

      // Corps `{ success: false }` en HTTP 200 (cours complet / doublon) :
      // `updateError` reste nul, la réactivation n'a pourtant pas eu lieu.
      const rejected = updateData?.success === false;
      if (updateError || rejected)  {
        console.error('[useBookingConf] Error updating booking:', updateError ?? updateData);
        const businessCode = getBookingBusinessErrorCode(updateError ?? updateData);
        if (businessCode === 'NO_SESSIONS_AVAILABLE') {
          bookingErrorToast(NO_SESSIONS_AVAILABLE_MESSAGE);
        } else if (businessCode === 'NO_VALID_SUBSCRIPTION_FOR_DATE') {
          bookingErrorToast(i18next.t('booking.noActiveSubscriptionForDate', { ns: 'bookingui' }));
        } else if (businessCode === 'CALENDAR_NOT_ALLOWED') {
          bookingErrorToast(CALENDAR_NOT_ALLOWED_MESSAGE);
        } else {
          bookingErrorToast(translate('booking.error', 'athlete'));
        }
        return false;
      }
    }
    else{  
      // Create the booking in the database
      const durationInt = typeof selectedClass.duration === 'string' 
        ? parseInt(selectedClass.duration, 10) 
        : (selectedClass.duration ?? 45);
      
      const gymId = selectedClass.gym_id || useUserStore.getState().gymId;
      
      if (!gymId) {
        console.error('[book_class_slot] Missing gym_id', {
          classId,
          selectedClass_gym_id: selectedClass.gym_id,
          userStore_gymId: useUserStore.getState().gymId
        });
        bookingErrorToast(translate('booking.gymError', 'athlete'));
        return false;
      }
      
      const toastId = 'booking-user';
      toast.loading(translate('booking.inProgress', 'athlete'), { id: toastId });

      let booked = false;
      let error;

      try {
        // Valeur de retour = « une ligne a-t-elle été créée ? ». Sans ce test,
        // une session expirée renvoyait `false` sans lever et le flux
        // journalisait BOOKING_CREATED, envoyait l'e-mail de confirmation et
        // toastait le succès pour une réservation inexistante (audit 3.1).
        booked = (await bookClassWithRetry({
          classId,
          userId: userId,
          date: formattedDate,
          time: selectedTime,
          duration: durationInt,
          gymId
        })) === true;
        toast.dismiss(toastId);
      } catch (err: any) {
        error = err;
        toast.dismiss(toastId);
      }

      if (!error && !booked) {
        console.error('[book_class_slot] returned falsy without error', { classId, userId });
        logUserActivity('BOOKING_FAILED', `classId:${classId},date:${formattedDate},time:${selectedTime},error:no_row_created`);
        bookingErrorToast(translate('booking.error', 'athlete'));
        return false;
      }

      if (error) {
        console.error('[book_class_slot] RPC error:', error, {
          code: (error as any).code,
          message: error.message,
          details: (error as any).details
        });
        
        logUserActivity('BOOKING_FAILED', `classId:${classId},date:${formattedDate},time:${selectedTime},error:${error.message?.slice(0, 150)}`);
        
        const businessCode = getBookingBusinessErrorCode(error);
        if (businessCode === 'NO_SESSIONS_AVAILABLE') {
          bookingErrorToast(NO_SESSIONS_AVAILABLE_MESSAGE);
        } else if (businessCode === 'NO_VALID_SUBSCRIPTION_FOR_DATE') {
          bookingErrorToast(i18next.t('booking.noActiveSubscriptionForDate', { ns: 'bookingui' }));
        } else if (businessCode === 'CALENDAR_NOT_ALLOWED') {
          bookingErrorToast(CALENDAR_NOT_ALLOWED_MESSAGE);
        } else if (error.message?.includes('Class does not belong')) {
          bookingErrorToast(i18next.t('booking.classNotInGym', { ns: 'bookingui' }));
        } else {
          bookingErrorToast(translate('booking.error', 'athlete'));
        }
        return false;
      }
    }

    logUserActivity("BOOKING_CREATED", `classId:${classId},user`);

    
    // ✅ OPTIMISATION : Envoi email NON-BLOQUANT (fire-and-forget)
    // `getUserEmail` peut renvoyer `null` : déstructurer directement levait
    // dans le `.then` (erreur avalée, e-mail jamais envoyé, sans trace claire).
    getUserEmail(userId).then((profileAuth) => {
      const userEmail = profileAuth?.email ?? null;
      supabase.functions.invoke('send-class-email', {
        body: {
          type: "booking",
          gym_id: gymId,
          user: {
            email: userEmail || '',
            firstName: userProfile?.first_name || '',
            lastName: userProfile?.last_name || ''
          },
          classInfo: {
            className: selectedClass.name,
            date: format(selectedDate, 'dd/MM/yyyy'),
            time: selectedTime,
            coach: selectedClass.instructor,
            duration: `${selectedClass.duration} min`
          }
        }
      }).catch(err => console.error('Error sending email confirmation:', err));
    }).catch(err => console.error('Error getting user email:', err));
    
    bookingSuccessToast(translate('booking.success', 'athlete'));
    return true;
  };

  return {
    confirmBooking
  };
};
